import { EntityDescriptor, EntityTablePage, EntityTablePagePartialProps, EntityTablePageReducers, EntityTablePageState, EntityTableSimpleProps, ITableActionParamForRun } from "@crispico/foundation-react";
import { RRCProps } from "@crispico/foundation-react/reduxReusableComponents/ReduxReusableComponents";
import { ContextMenuActionsProvider } from "components/ContextMenuRegistry";
import { EntityTablePageOptions, EntityTablePageOptionsButton } from "components/EntityTablePageOptionsButton";
import { IAction } from "@crispico/react-timeline-10000/types/components/ContextMenu/IAction";
import React, { ReactElement } from "react";
import { Table } from "fixed-data-table-2";
import { XopsGantt, XopsGanttProps, XopsGanttRRC } from "pages/gantt/XopsGantt";
import { OverrideableElement } from "@crispico/foundation-react/components/TabbedPage/TabbedPage";

export type XopsGanttPartialProps = Omit<XopsGanttProps, "id" | "table" | "tablePage" | "compactMode">;

export interface EntityTablePageWithGanttOptions extends EntityTablePageOptions {
    compactMode: boolean,
    showGantt: boolean,
    date: string | null
}

export class EntityTablePageWithGanttState<T extends EntityTablePageWithGanttOptions = EntityTablePageWithGanttOptions> extends EntityTablePageState {
    protected static readonly defaultOptions: EntityTablePageWithGanttOptions = { compactMode: false, showGantt: false, date: null };
    protected static readonly localStorageKey: string;

    options: T = (() => {
        const Class = this.constructor as typeof EntityTablePageWithGanttState;
        const { defaultOptions, localStorageKey, name } = Class;

        if (!localStorageKey) {
            throw new Error(`The class '${name}' must define the property 'localStorageKey'.`)
        }

        try {
            const raw = window.sessionStorage.getItem(localStorageKey);
            if (raw) {
                const lsOptions = JSON.parse(raw);
                return { ...defaultOptions, ...lsOptions } as T;
            }
        } catch (e) {
            // swallow
        }
        return defaultOptions as T;
    })();

    getLocalStorageKey: string = ((): string => {
        const Class = this.constructor as typeof EntityTablePageWithGanttState;
        return Class.localStorageKey;
    })();
}

export class EntityTablePageWithGanttReducers<S extends EntityTablePageWithGanttState = EntityTablePageWithGanttState> extends EntityTablePageReducers<S> { }

export type EntityTablePageWithGanttProps = EntityTablePagePartialProps & RRCProps<EntityTablePageWithGanttState, EntityTablePageWithGanttReducers>;

export abstract class EntityTablePageWithGantt<P extends EntityTablePageWithGanttProps = EntityTablePageWithGanttProps> extends EntityTablePage<P> {

    protected xopsGanttRef = React.createRef<XopsGantt>();
    protected contextMenuActionsProviderRef = React.createRef<ContextMenuActionsProvider>();

    constructor(props: P) {
        super(props);
        this.renderTimeline = this.renderTimeline.bind(this);
    }

    protected abstract getXopsGanttProps(): XopsGanttPartialProps;

    protected abstract getOptionsEntityDescriptor(): EntityDescriptor;

    protected provideActionsForRow(actionParam: ITableActionParamForRun): IAction[] {
        return [
            ...super.provideActionsForRow(actionParam),
            ...this.contextMenuActionsProviderRef.current!.getActions()
        ];
    }

    protected preRenderButtons(params: {}): Array<OverrideableElement> {
        const optionsEntityDescriptor = this.getOptionsEntityDescriptor();
        return [{
            element: <EntityTablePageOptionsButton key="options"
                entityDescriptor={optionsEntityDescriptor}
                options={this.props.s.options} onChange={async (options: EntityTablePageWithGanttOptions) => {
                    await this.props.r.setInReduxState({ options });
                    window.sessionStorage.setItem(`${this.props.s.getLocalStorageKey}`, JSON.stringify(options));
                    this.refresh();
                }} />
        }];
    }

    protected getTableSimpleProps(): Partial<EntityTableSimpleProps> | undefined {
        let props: Partial<EntityTableSimpleProps> = {};
        if (this.props.s.options?.showGantt) {
            props = {
                ...props,
                renderMainElementNormalModeFunction: this.renderTimeline,
                renderEmptyRow: () => null
            }
        };

        return props;
    }

    protected renderTimeline(table: ReactElement<Table>): ReactElement {
        return <XopsGanttRRC ref={this.xopsGanttRef} {...this.getXopsGanttProps()}
            id={`${this.props.entityDescriptor.name}XopsGantt`}
            tablePage={this} compactMode={this.props.s.options.compactMode}
            table={table} />
    }

    renderMain() {
        const that = this;
        return <>
            <ContextMenuActionsProvider ref={this.contextMenuActionsProviderRef}
                getEntityUid={(key: string | number) => `${this.props.entityDescriptor.name}:${key}`}
                getEntityAt={(key: string | number) => that.getEntityAt(key as number)}
                tablePage={this} />
            {super.renderMain()}
        </>
    }
}
